import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
} from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import DownloadIcon from "../../../../assets/icons/downloadIcon.svg";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import dayjs, { Dayjs } from "dayjs";
import ProfileHeader from "../../../../common/components/profile-header/profile-header-and-breadcrumbs";
import ListingTable from "../../../../common/components/listing-table/list-table";
import AdminPaginationComponent from "../../../../common/components/admin-pagination-component/admin-pagination-component";
import { AlertComponent } from "../../../../common/components";
import { routes } from "../../../../constants/routes";
import type { Header } from "../../../../common/components/listing-table/listing-table-props";
import BulkUploadPopup, {
  UploadOperationType,
  BulkUploadSubmitPayload,
  ProviderUploadSlotKey,
} from "./bulk-upload-popup";
import {
  bulkUploadProviderRegistrations,
  bulkUploadProviderServices,
  bulkUploadServices,
  bulkUploadSubServices,
  bulkUploadMerchandises,
  bulkUploadPlanners,
  getBulkUploadActivities,
} from "../../../../api/admin/bulk-upload";
import "../provider-management/provider-profiles/provider-profiles-list.scss";
import "./bulk-upload-operations.scss";

type UploadStatus = "Queued" | "Uploading" | "Processing" | "Completed" | "Failed" | "Completed Successfully" | "Completed with Errors";

interface BulkUploadJob {
  id: string;
  fileName: string;
  outputFileName?: string;
  fileSize: string;
  operationType: UploadOperationType;
  uploadedBy: string;
  uploadedAt: string;
  status: UploadStatus;
  progress: number;
  remarks: string;
  stageUpdatedAt: number;
  uploadedDate?: string;
  insertedRecords?: number;
  failedRecords?: number;
  alreadyExistsRecords?: number;
  fileURL?: string;
  errors?: any[];
}

const PAGE_SIZE = 10;

const formatFileSize = (bytes: number) => {
  if (!bytes) return "0 B";

  const units = ["B", "KB", "MB", "GB"];
  const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** unitIndex;

  return `${value.toFixed(value >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const getOutputFileName = (fileName: string) => {
  return fileName.replace(/\.(csv|xlsx)$/i, "") + "-output.csv";
};

const BulkUploadOperations: React.FC = () => {
  const [dbJobs, setDbJobs] = useState<BulkUploadJob[]>([]);
  const [uploadingJobs, setUploadingJobs] = useState<BulkUploadJob[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  const [selectedModule, setSelectedModule] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [selectedDate, setSelectedDate] = useState<string>("");

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<"error" | "success" | "info" | "warning">("info");

  const breadcrumbLink = [
    {
      label: "Admin Management",
      link: routes.ADMIN_MANAGEMENT,
      handleClick: () => { },
    },
    {
      label: "Bulk Upload Operations",
      link: "#",
      handleClick: () => { },
    },
  ];

  const fetchJobs = async () => {
    setIsLoading(true);
    try {
      const params: any = {
        page: currentPage,
        limit: PAGE_SIZE,
      };

      if (selectedDate) {
        params.fromDate = selectedDate;
        params.toDate = selectedDate;
        params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      }

      if (selectedModule) {
        if (selectedModule === "Provider") {
          params.uploadType = "Providers,Services,SubService,Merchandise,Provider Registration,Provider Services,provider_registration,provider_services,Provider Registrations";
        } else if (selectedModule === "Planner") {
          params.uploadType = "Planners";
        }
      }

      if (selectedStatus) {
        if (selectedStatus === "Completed") {
          params.status = "Completed Successfully,Completed with Errors";
        } else if (selectedStatus === "Failed") {
          params.status = "Failed";
        } else if (selectedStatus === "Pending") {
          params.status = "Pending,Queued";
        } else if (selectedStatus === "In progress") {
          params.status = "In progress,Uploading,Processing";
        }
      }

      const response = await getBulkUploadActivities(params);
      const data = response?.data?.data?.data || [];
      const pagination = response?.data?.data?.pagination || { totalCount: 0 };

      const mappedJobs = data.map((activity: any): BulkUploadJob => {
        let remarks = "-";
        let errorsArray: any[] = [];
        if (activity.errorMessage) {
          try {
            errorsArray = typeof activity.errorMessage === "string"
              ? JSON.parse(activity.errorMessage)
              : activity.errorMessage;
            if (Array.isArray(errorsArray) && errorsArray.length > 0) {
              remarks = errorsArray.map((e: any) => `Row ${e.row}: ${e.error}`).join("; ");
            } else if (typeof errorsArray === "object" && errorsArray !== null) {
              remarks = JSON.stringify(errorsArray);
            } else {
              remarks = String(errorsArray);
            }
          } catch (e) {
            remarks = String(activity.errorMessage);
          }
        }

        let operationType: UploadOperationType = "Provider";
        const typeLower = String(activity.uploadType).toLowerCase();
        if (typeLower === "planners" || typeLower === "planner") {
          operationType = "Planner";
        } else if (typeLower === "services" || typeLower === "service") {
          operationType = "Service";
        } else if (typeLower === "subservice") {
          operationType = "Subservice";
        } else if (typeLower === "merchandise") {
          operationType = "Merchandise";
        } else if (
          typeLower.includes("register") ||
          typeLower.includes("registration")
        ) {
          operationType = "Provider Registration";
        } else if (
          typeLower.includes("provider_services") ||
          typeLower.includes("provider-services") ||
          (typeLower.includes("provider") && typeLower.includes("service"))
        ) {
          operationType = "Provider Services";
        } else if (typeLower === "providers" || typeLower === "provider") {
          operationType = "Provider";
        }

        let status: UploadStatus = "Completed";
        if (activity.status === "Completed with Errors") {
          status = "Completed with Errors";
        } else if (activity.status === "Completed Successfully") {
          status = "Completed Successfully";
        } else if (activity.status === "Failed") {
          status = "Failed";
        }

        return {
          id: String(activity.id),
          fileName: activity.originalFilename,
          outputFileName: getOutputFileName(activity.originalFilename),
          fileSize: "",
          operationType,
          uploadedBy: activity.uploadedBy || "Admin",
          uploadedAt: activity.created_at,
          uploadedDate: activity.created_at ? String(activity.created_at).slice(0, 10) : undefined,
          status,
          progress: 100,
          remarks,
          stageUpdatedAt: new Date(activity.created_at).getTime(),
          insertedRecords: activity.insertedRecords,
          failedRecords: activity.failedRecords,
          alreadyExistsRecords: activity.alreadyExistsRecords,
          fileURL: activity.fileURL,
          errors: errorsArray,
        };
      });

      setDbJobs(mappedJobs);
      setTotalRecords(pagination.totalCount);
    } catch (error: any) {
      setAlertType("error");
      setAlertMessage("Failed to fetch bulk upload history.");
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [currentPage, selectedModule, selectedStatus, selectedDate]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedModule, selectedStatus, selectedDate]);

  const handleOpenDialog = () => {
    setOpenUploadDialog(true);
  };

  const handleUploadSubmit = async (payload: BulkUploadSubmitPayload) => {
    const tasks: Array<{
      key: string;
      label: string;
      file: File;
      apiCall: (file: File) => Promise<any>;
      operationType: UploadOperationType;
    }> = [];

    if (payload.operationType === "Provider") {
      if (payload.providerFiles.providerRegistration) {
        tasks.push({
          key: "providerRegistration",
          label: "Provider Registrations",
          file: payload.providerFiles.providerRegistration,
          apiCall: bulkUploadProviderRegistrations,
          operationType: "Provider Registration",
        });
      }
      if (payload.providerFiles.providerService) {
        tasks.push({
          key: "providerService",
          label: "Provider Services",
          file: payload.providerFiles.providerService,
          apiCall: bulkUploadProviderServices,
          operationType: "Provider Services",
        });
      }
      if (payload.providerFiles.subservice) {
        tasks.push({
          key: "subservice",
          label: "Subservices",
          file: payload.providerFiles.subservice,
          apiCall: bulkUploadSubServices,
          operationType: "Subservice",
        });
      }
      if (payload.providerFiles.merchandise) {
        tasks.push({
          key: "merchandise",
          label: "Merchandise Items",
          file: payload.providerFiles.merchandise,
          apiCall: bulkUploadMerchandises,
          operationType: "Merchandise",
        });
      }
      if (payload.providerFiles.service) {
        tasks.push({
          key: "service",
          label: "Services",
          file: payload.providerFiles.service,
          apiCall: bulkUploadServices,
          operationType: "Service",
        });
      }
    } else if (payload.operationType === "Planner") {
      if (payload.plannerFile) {
        tasks.push({
          key: "planner",
          label: "Planner",
          file: payload.plannerFile,
          apiCall: bulkUploadPlanners,
          operationType: "Planner",
        });
      }
    }

    if (tasks.length === 0) {
      setAlertType("warning");
      setAlertMessage("Select at least one CSV or XLSX file before uploading.");
      setAlertOpen(true);
      return;
    }

    const now = Date.now();
    const uploadedAt = new Date().toISOString();
    const uploadedDate = new Date().toISOString().slice(0, 10);

    const newJobs: BulkUploadJob[] = tasks.map((task) => ({
      id: `bulk-upload-${now}-${task.key}`,
      fileName: task.file.name,
      outputFileName: getOutputFileName(task.file.name),
      fileSize: formatFileSize(task.file.size),
      operationType: task.operationType,
      uploadedBy: "Admin",
      uploadedAt,
      status: "Queued",
      progress: 0,
      remarks: "Waiting in queue.",
      stageUpdatedAt: now,
      uploadedDate,
    }));

    setUploadingJobs((currentJobs) => [...newJobs, ...currentJobs]);
    setCurrentPage(1);
    setOpenUploadDialog(false);
    setAlertType("info");
    setAlertMessage(
      `Starting bulk upload of ${tasks.length} file${tasks.length > 1 ? "s" : ""}...`
    );
    setAlertOpen(true);

    const updateJobStatus = (
      jobId: string,
      status: UploadStatus,
      progress: number,
      remarks: string,
      extra?: { insertedRecords?: number; failedRecords?: number; alreadyExistsRecords?: number }
    ) => {
      setUploadingJobs((currentJobs) =>
        currentJobs.map((job) =>
          job.id === jobId
            ? {
              ...job,
              status,
              progress,
              remarks,
              stageUpdatedAt: Date.now(),
              ...(extra || {}),
            }
            : job
        )
      );
    };

    (async () => {
      let totalInserted = 0;
      let totalFailed = 0;
      let totalAlreadyExists = 0;

      for (let i = 0; i < tasks.length; i++) {
        const task = tasks[i];
        const jobId = `bulk-upload-${now}-${task.key}`;

        updateJobStatus(jobId, "Uploading", 30, `Uploading ${task.label} file...`);

        try {
          updateJobStatus(jobId, "Processing", 60, `Processing ${task.label} records...`);

          const response = await task.apiCall(task.file);
          const responseData = response?.data?.data || response?.data || {};
          const inserted = responseData?.insertedRecords ?? 0;
          const failed = responseData?.failedRecords ?? 0;
          const alreadyExists = responseData?.alreadyExistsCount ?? 0;

          totalInserted += inserted;
          totalFailed += failed;
          totalAlreadyExists += alreadyExists;

          updateJobStatus(
            jobId,
            "Completed Successfully",
            100,
            response?.data?.message || `${task.label} upload completed successfully.`,
            {
              insertedRecords: inserted,
              failedRecords: failed,
              alreadyExistsRecords: alreadyExists,
            }
          );
        } catch (error: any) {
          const apiErrorMessage =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            error?.message ||
            "An error occurred during upload.";

          updateJobStatus(jobId, "Failed", 0, apiErrorMessage);
        }
      }

      // After all uploads finish, clear the local temporary list and reload DB records to show the finalized state
      setUploadingJobs([]);
      fetchJobs();

      if (tasks.length > 0) {
        setAlertType(totalFailed > 0 ? "warning" : "success");
        setAlertMessage(`Upload finished. Inserted: ${totalInserted}, Failed: ${totalFailed}, Already Exists: ${totalAlreadyExists}.`);
        setAlertOpen(true);
      }
    })();
  };

  const handleOutputFileDownload = (job: BulkUploadJob) => {
    let fileContent = "";
    if (job.errors && job.errors.length > 0) {
      fileContent = [
        "Row Number,Error Message",
        ...job.errors.map(e => `${e.row},"${String(e.error).replace(/"/g, '""')}"`)
      ].join("\n");
    } else {
      fileContent = [
        "id,module,status,uploadedBy,uploadedAt,remarks",
        `${job.id},${job.operationType},${job.status},${job.uploadedBy},${job.uploadedAt},${job.remarks}`,
      ].join("\n");
    }

    const blob = new Blob([fileContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = job.outputFileName || getOutputFileName(job.fileName);
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const handleOriginalFileDownload = (job: BulkUploadJob) => {
    if (job.fileURL) {
      window.open(job.fileURL, "_blank");
      return;
    }

    const fileContent = [
      "id,module,status,uploadedBy,uploadedAt,remarks",
      `${job.id},${job.operationType},${job.status},${job.uploadedBy},${job.uploadedAt},${job.remarks}`,
    ].join("\n");

    const blob = new Blob([fileContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = job.fileName;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const displayedJobs = useMemo(() => {
    if (currentPage === 1) {
      return [...uploadingJobs, ...dbJobs];
    }
    return dbJobs;
  }, [uploadingJobs, dbJobs, currentPage]);

  const tableRows = useMemo(
    () =>
      displayedJobs.map((job, index) => ({
        id: job.id,
        failedRowsCount: job.failedRecords !== undefined ? String(job.failedRecords) : "-",
        alreadyExistsCount: job.alreadyExistsRecords !== undefined ? String(job.alreadyExistsRecords) : "-",
        operationType: job.operationType,
        uploadedBy: job.uploadedBy,
        uploadedAt: job.uploadedDate
          ? dayjs(job.uploadedDate).format("MM/DD/YYYY")
          : dayjs(job.uploadedAt).format("MM/DD/YYYY"),
        insertedRowsCount: job.insertedRecords !== undefined ? String(job.insertedRecords) : "-",
        status: job.status,
        remarks: job.remarks,
        action: "verticalEllipse",
        actionOptions: [
          {
            label: "Download File",
            value: "download_original",
            id: 1,
            disabled: false,
          },
          ...((job.errors && job.errors.length > 0) ? [{
            label: "Download Error Log",
            value: "download_errors",
            id: 2,
            disabled: false,
          }] : []),
        ],
      })),
    [displayedJobs, currentPage],
  );

  const headers: Header[] = [
    { key: "operationType", label: "Module Name", width: "9%" } as Header,
    { key: "status", label: "Status", width: "14%" } as Header,
    { key: "uploadedBy", label: "Uploaded By", width: "11%" } as Header,
    { key: "uploadedAt", label: "Uploaded Date", width: "8%" } as Header,
    { key: "remarks", label: "Failed Reason", width: "18%" } as Header,
    { key: "insertedRowsCount", label: "Inserted Rows Count", width: "9%" } as Header,
    { key: "failedRowsCount", label: "Failed Rows Count", width: "8%" } as Header,
    { key: "alreadyExistsCount", label: "Already Exists Count", width: "8%" } as Header,
    { key: "action", label: "Action", width: "5%" } as Header,
  ];

  const noResultsText = isLoading ? "Loading..." : "No data found";

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setCurrentPage(value);
  };

  const handleAction = (label: string, rowId: string, row: any) => {
    const job = displayedJobs.find(j => j.id === rowId);
    if (!job) return;
    if (label === "Download File") {
      handleOriginalFileDownload(job);
    } else if (label === "Download Error Log") {
      handleOutputFileDownload(job);
    }
  };

  return (
    <Box className="provider-profiles-main-wrapper" sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <ProfileHeader breadcrumbLink={breadcrumbLink} />

      <Box
        className="provider-profiles-filter-and-list-wrapper"
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          paddingBottom: displayedJobs.length === 0 ? "10px" : "0px",
          minHeight: 0,
        }}
      >
        <Box className="provider-profiles-search-and-filters bulk-upload-operations-header">
          <Box sx={{ display: "flex", justifyContent: "flex-end", width: "100%" }}>
            <Button
              variant="contained"
              startIcon={<UploadFileOutlinedIcon />}
              onClick={handleOpenDialog}
              className="bulk-upload-button"
            >
              Bulk Upload File
            </Button>
          </Box>
        </Box>

        <Box className="bulk-upload-filter-row">
          <FormControl size="small" className="bulk-upload-filter-item">
            <Select
              displayEmpty
              value={selectedModule}
              onChange={(event) => setSelectedModule(event.target.value)}
            >
              <MenuItem value="">Select a Module</MenuItem>
              <MenuItem value="Provider">Provider</MenuItem>
              <MenuItem value="Planner">Planner</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" className="bulk-upload-filter-item">
            <Select
              displayEmpty
              value={selectedStatus}
              onChange={(event) => setSelectedStatus(event.target.value)}
            >
              <MenuItem value="">Select a Status</MenuItem>
              <MenuItem value="Completed">Completed</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
              <MenuItem value="In progress">In progress</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
            </Select>
          </FormControl>

          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DesktopDatePicker
              value={selectedDate ? dayjs(selectedDate) : null}
              onChange={(newValue: Dayjs | null) => {
                if (!newValue || !newValue.isValid()) {
                  setSelectedDate("");
                  return;
                }
                setSelectedDate(newValue.format("YYYY-MM-DD"));
              }}
              format="MM/DD/YY"
              slotProps={{
                popper: {
                  sx: { zIndex: 2401 },
                },
                textField: {
                  placeholder: "Select a Date",
                  size: "small",
                  className: "bulk-upload-filter-item",
                  fullWidth: false,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Box className="provider-profiles-listing-table-wrapper">
          <ListingTable
            headers={headers}
            data={tableRows}
            noResultsText={noResultsText}
            handleAction={handleAction}
          />
        </Box>

        <AdminPaginationComponent
          totalRecords={Math.max(totalRecords + uploadingJobs.length, 1)}
          pageSize={PAGE_SIZE}
          currentPage={Math.min(currentPage, Math.max(Math.ceil(Math.max(totalRecords + uploadingJobs.length, 1) / PAGE_SIZE), 1))}
          onPageChange={handlePageChange}
        />
      </Box>

      <BulkUploadPopup
        open={openUploadDialog}
        onClose={() => setOpenUploadDialog(false)}
        onSubmit={handleUploadSubmit}
      />

      <AlertComponent
        open={alertOpen}
        type={alertType}
        message={alertMessage || ""}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default BulkUploadOperations;